<template>
  <div>
    <!-- 상세페이지 -->
    <div>
      <!-- header -->
      <div class="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h3>안전보건 교육 수강</h3>
        <div v-if="canRenew || (permission.c && permission.u && permission.d)" class="flex gap-3">
          <vs-button v-if="canRenew" color="#7E72F2" icon-pack="feather" icon="icon-refresh-cw" @click="onClickRenew">재수강</vs-button>
          <template v-if="permission.c && permission.u && permission.d">
            <vs-button type="border" @click="onClickDelete">삭제</vs-button>
            <vs-button color="secondary" @click="onClickEdit">수정</vs-button>
          </template>
        </div>
      </div>

      <!-- content-->
      <div class="writeGridContainer gap-2">
        <div class="writeGridLineItem">
          <div>교육대상</div>
          <div class="h-full flex items-center">{{ body.교육대상 }}</div>
        </div>
        <div class="writeGridLineItem">
          <div>교육종류</div>
          <div class="h-full flex items-center">{{ getVideoCodeText(body.videoCode) }}</div>
        </div>
        <div class="writeGridLineItem">
          <div>제목</div>
          <div class="h-full flex items-center">{{ body.제목 }}</div>
        </div>
        <div class="writeGridLineItem">
          <div>업로드일시</div>
          <div class="h-full flex items-center">{{ body.created_at }}</div>
        </div>
        <div v-if="permission.u" class="writeGridLineItem">
          <div>수강인원</div>
          <div class="h-full flex items-center gap-6">
            {{ (data && data.수강인원) || 0 }}
            <span class="flex items-center gap-1 cursor-pointer" style="color: #7e72f2" @click="onClickOpen"><feather-icon icon="EyeIcon" /> 수강인원</span>
          </div>
        </div>
        <div v-if="body.개요" class="writeGridLineItem">
          <div>개요</div>
          <div><div v-html="body.개요"></div></div>
        </div>
        <div class="writeGridLineItem">
          <div>교육영상</div>
          <div>
            <div id="playerContainer" class="relative" :style="{ paddingBottom: duration ? '56.25%' : '0' }">
              <div id="player" style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; height: 100%"></div>
            </div>
          </div>
        </div>
        <div class="writeGridLineItem">
          <div>시청시간</div>
          <div class="h-full flex flex-col justify-center gap-2 py-1">
            <div class="flex flex-wrap items-center gap-4">
              <span>{{ formatDuration(watchedSeconds) }}</span>
              <vs-button v-if="canRenew" class="renew-button" color="#7E72F2" icon-pack="feather" icon="icon-refresh-cw" @click="onClickRenew">재수강</vs-button>
            </div>
            <p v-if="renewNotice" class="renew-notice">{{ renewNotice }}</p>
          </div>
        </div>
      </div>
      <!-- //content-->

      <!-- 파일첨부 -->
      <file-upload-module v-if="body.fileList && body.fileList.length > 0" title="관련 자료" readonly v-model="body.fileList" class="mt-8" />
    </div>

    <!-- 수강인원 -->
    <vs-popup id="studentList" :active.sync="open" title="안전보건 교육 수강" :style="{ zIndex: 53010 }">
      <student-list ref="studentList" :title="body.제목" :videoPostId="videoPostId || 0" :info="body"/>
    </vs-popup>
  </div>
</template>

<script>
import FileUploadModule from "@/views/modules/FileUploadModule.vue";
import StudentList from "@/views/boards/single-page/1064/StudentList.vue";
import { getVideoCodeText, getRecordExpireDate, isRecordExpired, isRecordRenewing } from "@/util/TrainingVideo";

export default {
  components: { StudentList, FileUploadModule },
  props: {
    data: Object,
    permission: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    projectId() {
      return this.$route.params.proj_id;
    },
    boardId() {
      return this.$route.params.boardId;
    },
    body() {
      let body = {};
      if (this.data && this.data.body) {
        body = { ...this.data.body };
        body.created_at = this.data.created_at;
      }
      return body;
    },
    videoPostId() {
      if (this.data) {
        return this.data.id || 0;
      }
      return 0;
    },
    myInfo() {
      return this.$store.state.auth.userInfo;
    },
    // 수강내역(1065) body
    // data.수강내역은 부모에서 나중에 추가되는 속성이라 반응형이 아니므로, 로컬 상태(수강내역State)를 통해 참조한다.
    수강내역Body() {
      const 수강내역 = this.수강내역State;
      if (!수강내역 || !수강내역.post_content) return null;
      try {
        return JSON.parse(수강내역.post_content).body || null;
      } catch (e) {
        return null;
      }
    },
    // 수강내역이 본인 것인지 (author 정보가 없으면 본인 것으로 간주)
    isMyRecord() {
      const 수강내역 = this.수강내역State;
      if (!수강내역) return false;
      const authorId = 수강내역.author && 수강내역.author.id;
      if (authorId === undefined || authorId === null) return true;
      return parseInt(authorId) === parseInt(this.myInfo.id);
    },
    recordExpireDate() {
      return getRecordExpireDate(this.수강내역Body) || "";
    },
    isExpired() {
      return isRecordExpired(this.수강내역Body);
    },
    isRenewing() {
      return isRecordRenewing(this.수강내역Body);
    },
    // 본인의 합격 기록이 있고(만료 여부 무관), 아직 재수강을 시작하지 않은 경우에만 재수강 가능
    canRenew() {
      return this.isMyRecord && !!this.수강내역Body && this.수강내역Body.시험결과 === "합격" && !this.isRenewing;
    },
    // 시청시간 아래 안내 문구
    renewNotice() {
      const body = this.수강내역Body;
      if (!this.isMyRecord || !body || body.시험결과 !== "합격") return "";
      if (this.isRenewing) {
        return `재수강 중입니다. 영상을 다시 시청한 뒤 시험에 합격하면 교육이수일이 갱신됩니다. 합격 전까지는 기존 이수 기록(교육만료일 ${this.recordExpireDate})이 유지됩니다.`;
      }
      if (this.isExpired) {
        return `교육만료일(${this.recordExpireDate})이 지났습니다. 영상을 다시 시청하고 시험에 합격해야 현장에 투입될 수 있습니다.`;
      }
      return `교육만료일(${this.recordExpireDate}) 전에 미리 재수강할 수 있습니다. 재수강 중에도 기존 이수 기록은 교육만료일까지 유지됩니다.`;
    },
  },
  data() {
    return {
      // 현재 페이지
      page: 1,
      // 페이지당 게시글 수
      perPage: 10,
      // 마지막 페이지
      lastPage: 1,
      sort: "최신순",

      open: false,
      player: null,
      // 영상의 길이 (영상을 읽어 왔는지 여부로도 사용하고 있음)
      duration: undefined,
      // 마지막으로 보고된 시간 (초 단위)
      lastReportedTime: 0,
      // 누적 시청 시간 (초 단위)
      watchedSeconds: 0,
      // 플레이어 마지막 상태
      lastPlayerState: -1,
      // 수강내역(1065) 로컬 반응형 상태 (data.수강내역 과 동기화)
      수강내역State: null,
      // 플레이어 준비 후 자동 재생 대기 여부
      pendingAutoPlay: false,
    };
  },
  watch: {
    data: {
      immediate: true,
      handler(data) {
        this.수강내역State = (data && data.수강내역) || null;
      },
    },
  },
  mounted() {
    if (this.data && this.data.body && this.data.body.videoId) {
      this.start();
    }
  },
  methods: {
    getVideoCodeText,
    customAlert(msg) {
      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: "알림",
          message: msg,
        },
      });
      window.dispatchEvent(customAlert);
    },
    async start() {
      this.setWatchedSeconds();
      await this.youtubeLoad();
    },
    async end() {
      await this.updatePlayInfo();
      this.youtubePauseVideo(); // 유튜브 영상을 정지 시킨다.
    },
    setWatchedSeconds() {
      if (this.data.수강내역) {
        const 수강내역 = this.data.수강내역;
        const body = JSON.parse(수강내역.post_content).body;
        this.watchedSeconds = body.watchedSeconds;
      }
    },
    pause() {
      this.youtubePauseVideo();
    },

    async createPlayInfo() {
      if (parseInt(this.boardId) !== 1064) return;
      const form = {
        header: {
          year: new Date().getFullYear(),
          month: new Date().getMonth() + 1,
          date: new Date().format("yyyy-MM-dd"),
          half: new Date().getMonth() + 1 <= 6 ? "상반기" : "하반기", //반기
          quarter: `${Math.ceil((new Date().getMonth() + 1) / 3)}`, //분기
        },
        body: {
          videoPostId: this.data.id,
          watchedSeconds: 0, // 시청시간
          submit: [], // 제출한 답안
          수강완료: false, // 수강완료 여부
          시험결과: undefined, // 시험결과
        },
      };
      const result = await this.$store
        .dispatch("board/CREATE_POST", {
          projectId: this.projectId,
          boardId: 1065,
          title: `수강내역_${this.data.id}_${this.body.제목}`,
          content: JSON.stringify(form),
        })
        .catch((error) => {
          this.customAlert(error.response.data);
          return false;
        });

      if (result) {
        // 리스트 형식으로 데이터를 가져와야 통일되게 사용할 수 있어서,
        // 수강내역(1065) 다시 조회
        const listInfo = await this.$store.dispatch("board/LOAD_POST_LIST", {
          projectId: this.projectId,
          boardId: 1065,
          conditions: [
            { key: "user_id", operator: "=", value: this.myInfo.id },
            { key: "body.videoPostId", operator: "=", value: this.data.id },
          ],
          perPage: 1,
        });
        const 수강내역 = listInfo.data[0];
        this.$set(this.data, "수강내역", 수강내역);
        this.수강내역State = 수강내역;
      }
    },

    // 합격 기록이 만료된 상태에서 영상을 재생하면 자동으로 재수강을 시작한다.
    async startRenewIfExpired() {
      if (!this.isMyRecord || !this.isExpired || this.isRenewing) return false;
      await this.startRenew();
      return true;
    },

    // 재수강 시작 (만료 여부 무관)
    // 합격/교육이수일/교육만료일은 유지하고, 시청시간과 수강완료만 초기화한다.
    onClickRenew() {
      let text = `교육만료일(${this.recordExpireDate})까지는 기존 이수 기록이 유지됩니다. 영상을 다시 시청한 뒤 시험에 합격하면 교육이수일이 갱신됩니다. 재수강을 시작하시겠습니까?`;
      if (this.isExpired) {
        text = `교육만료일(${this.recordExpireDate})이 지났습니다. 영상을 다시 시청한 뒤 시험에 합격하면 교육이수일이 갱신됩니다. 재수강을 시작하시겠습니까?`;
      }
      this.$vs.dialog({
        type: "confirm",
        title: "재수강",
        text,
        acceptText: "예",
        cancelText: "아니오",
        accept: async () => {
          await this.startRenew();
        },
      });
    },
    async startRenew() {
      if (!this.data.수강내역) return;
      this.youtubePauseVideo();
      const postId = this.data.수강내역.id;
      const form = JSON.parse(this.data.수강내역.post_content);
      form.body.watchedSeconds = 0;
      form.body.수강완료 = false;
      form.body.갱신중 = true;
      form.body.갱신시작일 = new Date().format("yyyy-MM-dd");
      // 이전 시험 정보(답안, 출제 문제, 최근 시험결과)는 삭제한다.
      // 시험결과/교육이수일/교육만료일은 유지 (합격 전까지 기존 이수 기록 보존)
      form.body.submit = [];
      delete form.body.questions;
      delete form.body.최근시험결과;
      delete form.body.최근응시일;
      const result = await this.$store
        .dispatch("board/UPDATE_POST", {
          projectId: this.projectId,
          boardId: 1065,
          postId,
          title: `수강내역_${this.data.id}_${this.body.제목}`,
          content: JSON.stringify(form),
        })
        .catch((error) => {
          this.customAlert(error.response.data);
          return false;
        });
      if (result) {
        const updated = { ...this.data.수강내역, post_content: JSON.stringify(form) };
        this.$set(this.data, "수강내역", updated);
        this.수강내역State = updated;
        this.watchedSeconds = 0;
        // 재수강 시작과 동시에 영상을 처음부터 재생
        this.youtubePlayVideoFromStart();
      }
    },

    async updatePlayInfo() {
      const watchedSeconds = this.watchedSeconds;
      if (this.data.수강내역) {
        const postId = this.data.수강내역.id;
        const videoBody = JSON.parse(this.data.post_content).body;
        const form = JSON.parse(this.data.수강내역.post_content);
        form.body.watchedSeconds = Math.min(watchedSeconds, this.duration);
        form.body.수강완료 = form.body.watchedSeconds >= videoBody.watchedSecondsForPass;
        const result = await this.$store
          .dispatch("board/UPDATE_POST", {
            projectId: this.projectId,
            boardId: 1065,
            postId,
            title: `수강내역_${this.data.id}_${this.body.제목}`,
            content: JSON.stringify(form),
          })
          .catch((error) => {
            console.log("error", error)
            this.customAlert(error.response.data);
            return false;
          });

        if (result) {
          this.watchedSeconds = 0;
        }
      }
    },

    trackTime() {
      if (!this.player || typeof this.player.getCurrentTime !== "function") return;
      const current = Math.floor(this.player.getCurrentTime());
      this.watchedSeconds += 1;
      if (this.watchedSeconds > this.duration) {
        this.watchedSeconds = this.duration;
      }
      this.lastReportedTime = current;
    },
    onClickOpen() {
      this.open = true;
      this.$nextTick(() => {
        this.$refs.studentList.initLoadPostList();
      });
    },
    onClickEdit() {
      this.$emit("edit", this.data);
    },
    onClickDelete() {
      this.$emit("delete", this.data);
    },

    formatDuration(seconds) {
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60)
        .toString()
        .padStart(2, "0");
      return `${m}:${s}`;
    },

    // ################# YouTube 관련 메서드 #################
    async youtubeLoad() {
      const id = this.body.videoId;
      if (!id || typeof id !== "string" || id.length !== 11) {
        alert("유효한 YouTube URL을 입력하세요.");
        return;
      }
      this.body.videoId = id;
      await this.youtubeLoadApi();
      if (this.player) {
        // 기존 플레이어가 있다면 비디오를 새로 로드 (자동재생을 하려면 loadVideoById 메서드로 재생해야 함)
        if (this.player.cueVideoById) {
          this.player.cueVideoById(id);
        }
      } else {
        this.player = new YT.Player("player", {
          width: "100%",
          videoId: id,
          playerVars: {
            autoplay: 0,
          },
          events: {
            onReady: this.youtubePlayerReady,
            onStateChange: this.youtubePlayStateChange,
          },
        });
      }
    },
    // YouTube API 로드 (최초 1회만)
    youtubeLoadApi() {
      return new Promise((resolve) => {
        if (window.YT && window.YT.Player) {
          resolve();
        } else {
          const tag = document.createElement("script");
          tag.src = "https://www.youtube.com/iframe_api";
          const firstScriptTag = document.getElementsByTagName("script")[0];
          firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

          window.onYouTubeIframeAPIReady = () => {
            resolve();
          };
        }
      });
    },
    // YouTube 영상을 처음부터 재생 (재수강 시작 시)
    youtubePlayVideoFromStart() {
      if (!this.player) {
        // 플레이어가 아직 준비되지 않았으면 준비 완료 시 재생 (목록에서 재수강 시작한 경우)
        this.pendingAutoPlay = true;
        return;
      }
      this.pendingAutoPlay = false;
      if (typeof this.player.seekTo === "function") {
        this.player.seekTo(0, true);
      }
      if (typeof this.player.playVideo === "function") {
        this.player.playVideo();
      }
      // 플레이어가 보이도록 스크롤
      const el = document.getElementById("playerContainer");
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    // YouTube 영상 정지
    youtubePauseVideo() {
      if (this.player && typeof this.player.pauseVideo === "function") {
        // 재생 멈추면 타이머 중지
        clearInterval(this.timer);
        this.player.pauseVideo();
      }
    },
    youtubePlayStateChange(event) {
      const YTState = window.YT.PlayerState;

      if (event.data === YTState.CUED) {
        const duration = this.player.getDuration();
        if (duration > 0) {
          this.duration = duration;
        }
      }

      if ((this.lastPlayerState === YTState.UNSTARTED || this.lastPlayerState === YTState.CUED) && event.data === YTState.BUFFERING) {
        this.youtubeFirstPlayerState();
      }

      if (event.data === YTState.PLAYING) {
        // 1초마다 시청 시간 체크 시작
        if (!this.timer) {
          this.timer = setInterval(this.trackTime, 1000);
        }
      } else if (event.data === YTState.PAUSED || event.data === YTState.ENDED) {
        // 재생 멈추면 타이머 중지
        clearInterval(this.timer);
        this.timer = null;
      }
      this.lastPlayerState = event.data;
    },
    youtubePlayerReady(event) {
      // 영상 길이 저장(단위: 초)
      this.duration = this.player.getDuration();
      // 재수강 시작 후 플레이어 준비를 기다리고 있었다면 자동 재생
      if (this.pendingAutoPlay) {
        this.youtubePlayVideoFromStart();
      }
    },
    // 첫 플레이 상태
    async youtubeFirstPlayerState() {
      // 처음 플레이를 눌렀을 때 수강내역(1065)에 기록이 있는지 확인 뒤,
      // 기록이 없을 경우, 기록을 등록한다.

      // data.수강내역은 Page.vue::onClickDetail 에서 설정됨
      // 합격 기록이 있지만 교육만료일이 지난 경우, 재생 시 자동으로 재수강을 시작한다.
      // 만료 전 합격 기록은 "재수강" 버튼으로 미리 재수강을 시작할 수 있다.
      if (this.data) {
        if (this.data.수강내역) {
          await this.startRenewIfExpired();
        } else {
          await this.createPlayInfo();
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
#studentList::v-deep(.vs-popup) {
  width: 80vw;
  max-width: 1024px;
}

.renew-button::v-deep(.vs-button) {
  padding: 10px 22px;
  font-size: 15px;
  font-weight: 600;
}
.renew-button::v-deep(.vs-button--text) {
  font-size: 15px;
}
.renew-notice {
  margin: 0;
  font-size: 13px;
  color: #7e72f2;
  word-break: keep-all;
}
</style>
